import type { FhirResourceTypeString } from '@ctw/shared/api/fhir/types';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { prettyJsonStringifyObjectWithErrors } from '@ctw/shared/utils/json';
import { QUERY_KEY_PATIENT_SUMMARY_JOB_POLL } from '@ctw/shared/utils/query-keys';
import type { AnyJsonObj } from '@ctw/shared/utils/types';
import { parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MergeExclusive } from 'type-fest';

export type PatientSummaryJobStatus = 'running' | 'completed' | 'error';

export interface PatientSummaryJobCitation {
  id: number;
  resourceType: FhirResourceTypeString;
  resourceId: string;
  uid6: string;
}

interface PatientSummaryJobsApiFatalError {
  statusCode: string;
  statusText: string;
  statusDetails: string;
}

const isPatientSummaryJobsApiFatalError = (
  job: AnyJsonObj,
): job is PatientSummaryJobsApiFatalError => {
  return (
    Object.hasOwn(job, 'statusCode') &&
    Object.hasOwn(job, 'statusText') &&
    Object.hasOwn(job, 'statusDetails')
  );
};

interface PatientSummaryJobApiDataCommon {
  title: string;
  content: string;
}

interface PatientSummaryJobApiDataSuccess extends PatientSummaryJobApiDataCommon {
  status: Extract<PatientSummaryJobStatus, 'running' | 'completed'>;
  citations: Array<PatientSummaryJobCitation>;
}

interface PatientSummaryJobApiDataError extends PatientSummaryJobApiDataCommon {
  status: Extract<PatientSummaryJobStatus, 'error'>;
  errorMessage: string;
}

type PatientSummaryJobApiData = MergeExclusive<
  PatientSummaryJobApiDataSuccess,
  PatientSummaryJobApiDataError
>;

export const isPatientSummaryJobApiDataSuccess = (
  job: AnyJsonObj,
): job is PatientSummaryJobApiDataSuccess => {
  return ['running', 'completed'].includes(job.status);
};

export const isPatientSummaryJobApiDataError = (
  job: AnyJsonObj,
): job is PatientSummaryJobApiDataError => {
  return job.status === 'error';
};

export interface PatientSummaryJobsApiResponseSuccess {
  data: {
    type: 'summary-jobs';
    id: string;
    attributes: {
      status: Extract<PatientSummaryJobStatus, 'running' | 'completed'>;
      summary: Array<PatientSummaryJobApiData>;
      createdAt: string;
      updatedAt: string;
      summaryExpiresAt: string;
    };
    relationships: {
      patient: {
        data: {
          type: string;
          id: string;
        };
      };
    };
  };
}

interface PatientSummaryJobsApiResponseError {
  errors: Array<{
    status: Extract<PatientSummaryJobStatus, 'running' | 'completed'>;
    title: string;
    detail: string;
  }>;
}

type PatientSummaryJobsApiResponse = MergeExclusive<
  PatientSummaryJobsApiResponseSuccess,
  PatientSummaryJobsApiResponseError
>;

export const isPatientSummaryJobsApiResponseSuccess = (
  response: AnyJsonObj,
): response is PatientSummaryJobsApiResponseSuccess => {
  return response.data !== undefined;
};

export const isPatientSummaryJobsApiResponseError = (
  response: AnyJsonObj,
): response is PatientSummaryJobsApiResponseError => {
  return response.errors !== undefined;
};

export interface UsePatientSummariesOptions {
  /** Patient UPID. Required when autoSubmit is true. */
  patientUpid?: string;
  /** When false, the hook will not auto-submit a job on mount. Call `submitJob(patientUpid)` to trigger manually. Defaults to true. */
  autoSubmit?: boolean;
}

export interface UsePatientSummariesResponse {
  summaryJobId?: string;
  status: PatientSummaryJobStatus;
  summaries: Array<PatientSummaryJobApiData>;
  lastRefreshed?: Date;
  error?: PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponseError;
  /** Manually trigger a summary job submission. Provide the patient UPID for the job. */
  submitJob: (patientUpid: string) => void;
  /** Reset the hook state, stopping any in-progress polling. */
  reset: () => void;
  /** Check for a cached summary via HEAD request. Returns the cached status or null if not found. */
  checkCachedSummary: (patientUpid: string) => Promise<'running' | 'completed' | null>;
}

export const usePatientSummaries = (
  options?: UsePatientSummariesOptions,
): UsePatientSummariesResponse => {
  const autoSubmit = options?.autoSubmit !== false;
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();

  const [summaryJobsResponse, setSummaryJobsResponse] = useState<
    PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponse | null
  >(null);
  const [activeUpid, setActiveUpid] = useState<string | undefined>(options?.patientUpid);
  const submitInitiatedRef = useRef(false);

  const submitPatientSummaryJob = useCallback(
    async (
      patientUpid: string,
    ): Promise<PatientSummaryJobsApiFatalError | PatientSummaryJobsApiResponse> => {
      try {
        const response = await fetch(
          `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${requestContext.authToken}`,
              'Zus-Account': requestContext.builderId,
            },
            body: JSON.stringify({
              data: {
                relationships: {
                  patient: {
                    data: {
                      id: patientUpid,
                    },
                  },
                },
              },
            }),
          },
        );

        const responseText = await response.text();

        if (!response.ok) {
          return {
            statusCode: response.status.toString(),
            statusText: response.statusText,
            statusDetails: responseText,
          };
        }

        let responseJson: AnyJsonObj | null = null;
        try {
          responseJson = JSON.parse(responseText);
        } catch {
          /* pass */
        }

        if (!responseJson) {
          return {
            statusCode: 'invalid_non_json_response',
            statusText: 'Non-JSON response from server',
            statusDetails: responseText,
          };
        }

        if (
          isPatientSummaryJobsApiResponseSuccess(responseJson) ||
          isPatientSummaryJobsApiResponseError(responseJson)
        ) {
          return responseJson;
        }

        return {
          statusCode: 'invalid_json_response',
          statusText: 'JSON response from server did not match expected schema',
          statusDetails: responseText,
        };
      } catch (error: unknown) {
        const errorMessage = 'Unknown Exception submitting patient summary job';
        telemetry.trackError({
          message: errorMessage,
          error: error instanceof Error ? error : new Error(errorMessage),
          context: {
            error,
            upid: patientUpid,
          },
        });

        return {
          statusCode: 'unknown_exception',
          statusText: error instanceof Error ? error.name : 'unknown',
          statusDetails:
            error instanceof Error ? error.message : prettyJsonStringifyObjectWithErrors(error),
        };
      }
    },
    [requestContext, telemetry],
  );

  const currentJobId =
    summaryJobsResponse !== null && isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse)
      ? summaryJobsResponse.data.id
      : undefined;

  const summaryJobPollingQuery = useCtwQuery({
    enabled:
      summaryJobsResponse !== null &&
      isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse) &&
      summaryJobsResponse.data.attributes.status === 'running',
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
    queryId: QUERY_KEY_PATIENT_SUMMARY_JOB_POLL,
    queryKey: [currentJobId, activeUpid],
    queryFn: async ({ requestContext }) => {
      if (
        summaryJobsResponse !== null &&
        isPatientSummaryJobsApiResponseSuccess(summaryJobsResponse)
      ) {
        try {
          const response = await fetch(
            `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs/${summaryJobsResponse.data.id}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${requestContext.authToken}`,
                'Zus-Account': requestContext.builderId,
              },
            },
          );

          const responseText = await response.text();

          if (!response.ok) {
            return {
              statusCode: response.status.toString(),
              statusText: response.statusText,
              statusDetails: responseText,
            };
          }

          let responseJson: AnyJsonObj | null = null;
          try {
            responseJson = JSON.parse(responseText);
          } catch {
            /* pass */
          }

          if (!responseJson) {
            return {
              statusCode: 'invalid_non_json_response',
              statusText: 'Non-JSON response from server',
              statusDetails: responseText,
            };
          }

          if (
            isPatientSummaryJobsApiResponseSuccess(responseJson) ||
            isPatientSummaryJobsApiResponseError(responseJson)
          ) {
            return responseJson;
          }

          return {
            statusCode: 'invalid_json_response',
            statusText: 'JSON response from server did not match expected schema',
            statusDetails: responseText,
          };
        } catch (error: unknown) {
          const errorMessage = 'Unknown Exception polling patient summary job';
          telemetry.trackError({
            message: errorMessage,
            error: error instanceof Error ? error : new Error(errorMessage),
            context: {
              error,
              upid: activeUpid,
              summaryJobId: summaryJobsResponse.data.id,
            },
          });

          return {
            statusCode: 'unknown_exception',
            statusText: error instanceof Error ? error.name : 'unknown',
            statusDetails:
              error instanceof Error ? error.message : prettyJsonStringifyObjectWithErrors(error),
          };
        }
      }

      return undefined;
    },
  });

  const submitAbortControllerRef = useRef<AbortController | null>(null);

  const submitJob = useCallback(
    (patientUpid: string) => {
      // Abort any in-flight submission to prevent stale responses from overwriting state.
      submitAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      submitAbortControllerRef.current = abortController;

      setSummaryJobsResponse(null);
      setActiveUpid(patientUpid);
      submitPatientSummaryJob(patientUpid).then((response) => {
        if (!abortController.signal.aborted) {
          setSummaryJobsResponse(response);
        }
      });
    },
    [submitPatientSummaryJob],
  );

  const reset = useCallback(() => {
    setSummaryJobsResponse(null);
    setActiveUpid(undefined);
    submitInitiatedRef.current = false;
  }, []);

  const checkCachedSummary = useCallback(
    async (patientUpid: string): Promise<'running' | 'completed' | null> => {
      // Reset any existing job state (stops polling for a previous patient).
      setSummaryJobsResponse(null);
      setActiveUpid(patientUpid);

      try {
        const response = await fetch(
          `${getAiServiceBaseUrl({ env: requestContext.env })}/summary-jobs?upid=${patientUpid}`,
          {
            method: 'HEAD',
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Zus-Account': requestContext.builderId,
            },
          },
        );

        if (response.status === 404) {
          return null;
        }

        if (response.ok) {
          const jobId = response.headers.get('X-Job-Id');
          const jobStatus = response.headers.get('X-Job-Status') as 'running' | 'completed' | null;

          if (jobId && (jobStatus === 'completed' || jobStatus === 'running')) {
            setActiveUpid(patientUpid);
            setSummaryJobsResponse({
              data: {
                type: 'summary-jobs',
                id: jobId,
                attributes: {
                  status: jobStatus,
                  summary: [],
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  summaryExpiresAt: new Date().toISOString(),
                },
                relationships: {
                  patient: { data: { type: 'fhir/Patient', id: patientUpid } },
                },
              },
            });
            return jobStatus;
          }
        }
      } catch (_error) {
        // Silently fail — user can still generate manually
      }

      return null;
    },
    [requestContext],
  );

  useEffect(() => {
    if (
      autoSubmit &&
      options?.patientUpid &&
      summaryJobsResponse === null &&
      !submitInitiatedRef.current
    ) {
      submitInitiatedRef.current = true;
      setActiveUpid(options.patientUpid);
      submitPatientSummaryJob(options.patientUpid).then((response) =>
        setSummaryJobsResponse(response),
      );
    } else if (summaryJobPollingQuery.data !== undefined) {
      setSummaryJobsResponse(summaryJobPollingQuery.data);
    }
  }, [
    autoSubmit,
    options?.patientUpid,
    submitPatientSummaryJob,
    summaryJobPollingQuery.data,
    summaryJobsResponse,
  ]);

  return useMemo(() => {
    if (summaryJobsResponse === null) {
      return {
        status: 'running' as const,
        summaries: [],
        submitJob,
        reset,
        checkCachedSummary,
      };
    }

    // Fatal error submitting jobs request
    if (isPatientSummaryJobsApiFatalError(summaryJobsResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: summaryJobsResponse,
        submitJob,
        reset,
        checkCachedSummary,
      } satisfies UsePatientSummariesResponse;
    }

    // Response error submitting jobs request
    if (isPatientSummaryJobsApiResponseError(summaryJobsResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: summaryJobsResponse,
        submitJob,
        reset,
        checkCachedSummary,
      } satisfies UsePatientSummariesResponse;
    }

    // Prefer polling data when available; fall back to the initial POST response
    // (e.g. when the API returned an already-completed cached job and polling never started).
    const latestResponse = summaryJobPollingQuery.data ?? summaryJobsResponse;

    // Fatal error polling job endpoint
    if (isPatientSummaryJobsApiFatalError(latestResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: latestResponse,
        submitJob,
        reset,
        checkCachedSummary,
      } satisfies UsePatientSummariesResponse;
    }

    // Response error polling job endpoint
    if (isPatientSummaryJobsApiResponseError(latestResponse)) {
      return {
        status: 'error',
        summaries: [],
        error: latestResponse,
        submitJob,
        reset,
        checkCachedSummary,
      } satisfies UsePatientSummariesResponse;
    }

    return {
      summaryJobId: latestResponse.data.id,
      status: latestResponse.data.attributes.status,
      summaries: latestResponse.data.attributes.summary,
      lastRefreshed: parseISO(latestResponse.data.attributes.updatedAt),
      submitJob,
      reset,
      checkCachedSummary,
    } satisfies UsePatientSummariesResponse;
  }, [summaryJobsResponse, summaryJobPollingQuery, submitJob, reset, checkCachedSummary]);
};
